import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

import { fetchMiniMap } from '../services/reportsService';
import { useError } from '../contexts/ErrorContext';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import { sortVersionsDesc } from '../utils/versionSort';
import { COMPLETED_COLOR, REASON_COLOR } from '../constants/blobFailureReasons';
import './MiniMapReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
})();

const colorForOutcome = (key) => (key === 'completed' ? COMPLETED_COLOR : REASON_COLOR[key.slice('failed:'.length)] || '#8e8e93');

const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
const fmtSec = (v) => (v == null ? '—' : `${v.toFixed(2)}s`);

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="mm-multiselect" ref={ref}>
      <button className="mm-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`mm-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="mm-multiselect-dropdown">
          <div className="mm-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="mm-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="mm-multiselect-option">
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OutcomeTooltip = ({ active, payload, label, outcomes }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const present = outcomes.filter((o) => (row.byOutcome?.[o.key]?.count || 0) > 0);
  return (
    <div className="mm-tooltip">
      <div className="mm-tooltip-date">{label}</div>
      {present.map((o) => (
        <div key={o.key} className="mm-tooltip-row">
          <span className="mm-tooltip-swatch" style={{ background: colorForOutcome(o.key) }} />
          <span className="mm-tooltip-label">{o.label}</span>
          <span className="mm-tooltip-value">{(row.byOutcome?.[o.key]?.count || 0).toLocaleString()}</span>
        </div>
      ))}
      <div className="mm-tooltip-divider" />
      <div className="mm-tooltip-row">
        <span className="mm-tooltip-label">Total</span>
        <span className="mm-tooltip-value">{(row.total || 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

const HistogramTooltip = ({ active, payload, label, color }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="mm-tooltip">
      <div className="mm-tooltip-date">{label}</div>
      <div className="mm-tooltip-row">
        <span className="mm-tooltip-swatch" style={{ background: color }} />
        <span className="mm-tooltip-label">Transfers</span>
        <span className="mm-tooltip-value">{(payload[0]?.value || 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

// Marks a label whose value excludes cache-hit runs — every duration figure
// on this report except the Failed histogram (a fast failure isn't a cache
// hit, the peripheral just rejected it quickly).
const CacheMark = ({ threshold }) => (
  <sup className="mm-cache-mark" title={`Excludes cache hit runs (<${threshold}s)`}>*</sup>
);

const MiniMapReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [allAppVersions, setAllAppVersions] = useState([]);
  const [allFirmwareVersions, setAllFirmwareVersions] = useState([]);
  const [selectedAppVersions, setSelectedAppVersions] = useState([]);
  const [selectedFirmware, setSelectedFirmware] = useState([]);

  // A filter selection is only valid for the date range it was populated
  // from (convention #2) — reusing it after the range changes can silently
  // zero every outcome if the old versions don't exist in the new range.
  const resetFilters = useCallback(() => {
    setAllAppVersions([]);
    setAllFirmwareVersions([]);
    setSelectedAppVersions([]);
    setSelectedFirmware([]);
  }, []);

  const handleFetch = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        appVersions: overrides.appVersions ?? selectedAppVersions,
        gogglesFirmwareVersions: overrides.gogglesFirmwareVersions ?? selectedFirmware,
      };
      const result = await fetchMiniMap(startDate, endDate, addError, filters);
      setData(result);

      // Populate filter dropdowns from the first unfiltered fetch's own
      // response (convention #2).
      if (allAppVersions.length === 0 && result.filterOptions) {
        const appVers = sortVersionsDesc(result.filterOptions.appVersions || []);
        const fwVers = sortVersionsDesc(result.filterOptions.gogglesFirmwareVersions || []);
        if (appVers.length) {
          setAllAppVersions(appVers);
          setAllFirmwareVersions(fwVers);
          setSelectedAppVersions(appVers);
          setSelectedFirmware(fwVers);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch Mini Map data.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, addError, selectedAppVersions, selectedFirmware, allAppVersions.length]);

  const handleApplyFilters = useCallback(() => {
    handleFetch({ appVersions: selectedAppVersions, gogglesFirmwareVersions: selectedFirmware });
  }, [handleFetch, selectedAppVersions, selectedFirmware]);

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleFetch(); };

  const outcomes = useMemo(() => data?.outcomes || [], [data]);

  // Only chart/legend outcomes that actually occurred in range — with
  // fixed failure reasons and (typically) very few real failures, listing
  // every reason in the legend would mostly be noise. The error-breakdown
  // table below still shows every reason, including the zero-count ones.
  const outcomeTotals = useMemo(() => {
    const totals = Object.fromEntries(outcomes.map((o) => [o.key, 0]));
    for (const day of data?.dailyByOutcome || []) {
      for (const key of Object.keys(totals)) totals[key] += day.byOutcome?.[key]?.count || 0;
    }
    return totals;
  }, [data, outcomes]);
  const presentOutcomes = useMemo(
    () => outcomes.filter((o) => o.key === 'completed' || outcomeTotals[o.key] > 0),
    [outcomes, outcomeTotals]
  );

  const chartData = data?.dailyByOutcome || [];
  const hasRows = chartData.length > 0;

  const histogramBuckets = data?.durationHistogram?.buckets || [];
  const completedHistData = histogramBuckets.map((b) => ({ bucket: b.label, count: data.durationHistogram.completed[b.key] || 0 }));
  const failedHistData = histogramBuckets.map((b) => ({ bucket: b.label, count: data.durationHistogram.failed[b.key] || 0 }));

  const errorReasons = data?.errorReasons || [];
  const byResort = data?.byResort || [];

  return (
    <div className="mm-report">
      <div className="mm-header">
        <div className="mm-header-left">
          <h1 className="mm-title">Mini Map</h1>
          <p className="mm-subtitle">Map vector-tile (.bin) BLE transfer — outcome, failure reason, duration, and ski area</p>
        </div>
        <div className="mm-controls">
          <input
            type="date"
            className="mm-date-input"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); resetFilters(); }}
            onKeyDown={handleKeyDown}
          />
          <span className="mm-date-sep">to</span>
          <input
            type="date"
            className="mm-date-input"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); resetFilters(); }}
            onKeyDown={handleKeyDown}
          />
          <button className="mm-fetch-btn" onClick={() => handleFetch()} disabled={loading}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {allAppVersions.length > 0 && (
        <div className="mm-filters">
          <MultiSelect label="App Version" options={allAppVersions} selected={selectedAppVersions} onChange={setSelectedAppVersions} />
          <MultiSelect label="Goggles Firmware" options={allFirmwareVersions} selected={selectedFirmware} onChange={setSelectedFirmware} />
          <button className="mm-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="mm-error">{error}</div>}

      {loading && (
        <div className="mm-loading">
          <div className="mm-spinner" />
          Querying BigQuery...
        </div>
      )}

      {!loading && !data && !error && (
        <div className="mm-empty">Select a date range and click Run Report.</div>
      )}

      {!loading && data && (
        <div className="mm-content">
          {/* Cache hit callout */}
          {data.summary.cache_hit_rate != null && (
            <div className="mm-callout">
              <div className="mm-callout-stat">
                <span className="mm-callout-value">{fmtPct(data.summary.cache_hit_rate)}</span>
                <span className="mm-callout-label">Cache Hit Rate</span>
              </div>
              <p className="mm-callout-text">
                Completed transfers under {data.cacheHitThresholdS}s are cache hits — the goggles already had this
                build. Duration stats below (median, P90/P95, and the histogram) exclude them.
              </p>
            </div>
          )}

          {/* Summary cards */}
          <div className="mm-summary">
            <div className="mm-card">
              <span className="mm-card-value">{data.summary.total_begin_attempts.toLocaleString()}</span>
              <span className="mm-card-label">Begin Attempts</span>
            </div>
            <div className="mm-card">
              <span className="mm-card-value" style={{ color: COMPLETED_COLOR }}>{data.summary.total_completed.toLocaleString()}</span>
              <span className="mm-card-label">Completed</span>
            </div>
            <div className="mm-card">
              <span className="mm-card-value" style={{ color: '#ff375f' }}>{data.summary.total_failed.toLocaleString()}</span>
              <span className="mm-card-label">Failed</span>
            </div>
            <div className="mm-card">
              <span className="mm-card-value">{fmtPct(data.summary.success_rate)}</span>
              <span className="mm-card-label">Success Rate</span>
            </div>
            <div className="mm-card">
              <span className="mm-card-value">{fmtSec(data.summary.median_duration_s)}</span>
              <span className="mm-card-label">Median Duration (Success)<CacheMark threshold={data.cacheHitThresholdS} /></span>
            </div>
            <div className="mm-card">
              <span className="mm-card-value">{fmtSec(data.summary.p90_duration_s)}</span>
              <span className="mm-card-label">P90 Duration (Success)<CacheMark threshold={data.cacheHitThresholdS} /></span>
            </div>
          </div>

          {/* Outcome chart */}
          <div className="mm-section">
            <div className="mm-section-header">
              <h3 className="mm-section-title">Daily Outcome</h3>
              <div className="mm-legend">
                {presentOutcomes.map((o) => (
                  <span key={o.key} className="mm-legend-item">
                    <span className="mm-legend-swatch" style={{ background: colorForOutcome(o.key) }} />
                    {o.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="mm-chart-container">
              {hasRows ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid stroke="#1f1f1f" vertical={false} />
                    <XAxis dataKey="date" stroke="#444" tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }} tickFormatter={(v) => v?.slice(5)} />
                    <YAxis stroke="#444" allowDecimals={false} tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }} />
                    <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={(props) => <OutcomeTooltip {...props} outcomes={presentOutcomes} />} />
                    {presentOutcomes.map((o) => (
                      <Bar
                        key={o.key}
                        dataKey={(row) => row.byOutcome?.[o.key]?.count || 0}
                        stackId="outcome"
                        name={o.label}
                        fill={colorForOutcome(o.key)}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="mm-chart-empty">No map transfers in this date range.</div>
              )}
            </div>
          </div>

          {/* Error breakdown */}
          <div className="mm-section">
            <div className="mm-section-header">
              <h3 className="mm-section-title">Error Breakdown <span className="mm-section-sub">(all {errorReasons.length} known reasons, including zero-count)</span></h3>
            </div>
            <div className="mm-table">
              <div className="mm-reason-table-header mm-reason-row-grid">
                <span>Reason</span>
                <span>Count</span>
                <span>Share of Failures</span>
              </div>
              <div className="mm-table-body">
                {errorReasons.map((r) => (
                  <div key={r.key} className="mm-reason-row mm-reason-row-grid">
                    <span className="mm-reason-label">
                      <span className="mm-reason-swatch" style={{ background: REASON_COLOR[r.key] }} />
                      {r.label}
                    </span>
                    <span className="mm-cell-mono">{r.count.toLocaleString()}</span>
                    <span className="mm-cell-mono">{r.count > 0 ? fmtPct(r.share) : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Duration histograms */}
          <div className="mm-section">
            <div className="mm-section-header">
              <h3 className="mm-section-title">Transfer Duration</h3>
            </div>
            <div className="mm-histogram-grid">
              <div>
                <div className="mm-histogram-header">
                  <span className="mm-histogram-label" style={{ color: COMPLETED_COLOR }}>
                    Completed (Success)<CacheMark threshold={data.cacheHitThresholdS} />
                  </span>
                  <span className="mm-histogram-stat">
                    Median {fmtSec(data.summary.median_duration_s)} &middot; P90 {fmtSec(data.summary.p90_duration_s)}
                  </span>
                </div>
                <div className="mm-chart-container">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={completedHistData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid stroke="#1f1f1f" vertical={false} />
                      <XAxis dataKey="bucket" stroke="#444" tick={{ fill: '#999', fontSize: 10, fontFamily: 'SF Mono, Monaco, monospace' }} />
                      <YAxis stroke="#444" allowDecimals={false} tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }} />
                      <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={(props) => <HistogramTooltip {...props} color={COMPLETED_COLOR} />} />
                      <Bar dataKey="count" fill={COMPLETED_COLOR} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <div className="mm-histogram-label" style={{ color: '#ff375f' }}>Failed</div>
                <div className="mm-chart-container">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={failedHistData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid stroke="#1f1f1f" vertical={false} />
                      <XAxis dataKey="bucket" stroke="#444" tick={{ fill: '#999', fontSize: 10, fontFamily: 'SF Mono, Monaco, monospace' }} />
                      <YAxis stroke="#444" allowDecimals={false} tick={{ fill: '#999', fontSize: 11, fontFamily: 'SF Mono, Monaco, monospace' }} />
                      <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} content={(props) => <HistogramTooltip {...props} color="#ff375f" />} />
                      <Bar dataKey="count" fill="#ff375f" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Per-resort table */}
          <div className="mm-section">
            <div className="mm-section-header">
              <h3 className="mm-section-title">By Resort</h3>
            </div>
            <div className="mm-table">
              <div className="mm-resort-table-header mm-resort-row-grid">
                <span>Resort</span>
                <span>Begin Attempts</span>
                <span>Completed</span>
                <span>Failed</span>
                <span>Success Rate</span>
                <span>Top Failure Reason</span>
                <span>Median Duration<CacheMark threshold={data.cacheHitThresholdS} /></span>
                <span>P95 Duration<CacheMark threshold={data.cacheHitThresholdS} /></span>
              </div>
              <div className="mm-table-body">
                {byResort.length === 0 ? (
                  <div className="mm-day-empty">No resorts with map transfer activity in this range.</div>
                ) : byResort.map((r) => (
                  <div key={r.resortId} className="mm-resort-row mm-resort-row-grid">
                    <span className="mm-resort-name" title={r.resortId}>
                      {r.resortName || r.resortId}
                      <span className="mm-resort-id">{r.resortId}</span>
                    </span>
                    <span className="mm-cell-mono">{r.beginAttempts.toLocaleString()}</span>
                    <span className="mm-cell-mono" style={{ color: COMPLETED_COLOR }}>{r.completed.toLocaleString()}</span>
                    <span className="mm-cell-mono" style={{ color: r.failed > 0 ? '#ff375f' : null }}>{r.failed.toLocaleString()}</span>
                    <span className="mm-cell-mono">{fmtPct(r.successRate)}</span>
                    <span className="mm-cell-reason">
                      {r.topFailureReason ? (
                        <>
                          <span className="mm-reason-swatch" style={{ background: REASON_COLOR[r.topFailureReason.key] }} />
                          {r.topFailureReason.label}
                        </>
                      ) : '—'}
                    </span>
                    <span className="mm-cell-mono">{fmtSec(r.medianDurationS)}</span>
                    <span className="mm-cell-mono">{fmtSec(r.p95DurationS)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mm-footnote">
            * Excludes cache hit runs (&lt;{data.cacheHitThresholdS}s) — see Cache Hit Rate above.
          </p>
        </div>
      )}
    </div>
  );
};

export default MiniMapReport;
